/**
 * 畫廊參數設置 UI
 * 允許用戶調整畫廊顯示參數和 PDF 導出設置
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Radio, Space, Divider, Typography, Button } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useLanguage } from '../../../../contexts/LanguageContext';

const { Text } = Typography;

const GallerySettings = ({ settings, onSettingsChange, open, onClose }) => {
  const { t } = useLanguage();
  const [form] = Form.useForm();

  // 當外部 settings 改變時，更新表單
  useEffect(() => {
    if (open) {
      form.setFieldsValue(settings);
    }
  }, [settings, form, open]);

  const handleOk = () => {
    form.validateFields().then(values => {
      if (onSettingsChange) {
        onSettingsChange(values);
      }
      if (onClose) {
        onClose();
      }
    }).catch(err => {
      console.error('Validation failed:', err);
    });
  };

  const handleValuesChange = (changedValues, allValues) => {
    // 實時更新設置（可選，如果需要實時更新）
    // if (onSettingsChange) {
    //   onSettingsChange(allValues);
    // }
  };

  return (
    <Modal
      title={t('reports.printOutSettings') || 'Print Out Settings'}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={t('common.confirm') || '確定'}
      cancelText={t('common.cancel') || '取消'}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          maxImagesPerPage: 4,
          imageGap: 5,
          minImageWidth: 50,
          pdfOrientation: 'landscape',
          imageQuality: 0.9
        }}
        onValuesChange={handleValuesChange}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ fontSize: '13px' }}>
              {t('reports.displaySettings') || '顯示設置'}
            </Text>
            <Divider style={{ margin: '8px 0' }} />
            
            <Form.Item
              label={t('reports.maxImagesPerPage') || '每頁最多圖片數量'}
              name="maxImagesPerPage"
              tooltip={t('reports.maxImagesPerPageTooltip') || '控制每頁最多顯示的圖片數量，實際數量會根據圖片大小自動調整'}
            >
              <InputNumber
                min={1}
                max={10}
                style={{ width: '100%' }}
                placeholder="4"
              />
            </Form.Item>

            <Form.Item
              label={t('reports.imageGap') || '圖片間距（mm）'}
              name="imageGap"
              tooltip={t('reports.imageGapTooltip') || 'PDF 導出時圖片之間的間距'}
            >
              <InputNumber
                min={0}
                max={20}
                style={{ width: '100%' }}
                placeholder="5"
                addonAfter="mm"
              />
            </Form.Item>

            <Form.Item
              label={t('reports.minImageWidth') || '最小圖片寬度（mm）'}
              name="minImageWidth"
              tooltip={t('reports.minImageWidthTooltip') || '防止圖片過小看不清，PDF 導出時圖片不會小於此寬度'}
            >
              <InputNumber
                min={10}
                max={200}
                style={{ width: '100%' }}
                placeholder="50"
                addonAfter="mm"
              />
            </Form.Item>
          </div>

          <div>
            <Text strong style={{ fontSize: '13px' }}>
              {t('reports.pdfExportSettings') || 'PDF 導出設置'}
            </Text>
            <Divider style={{ margin: '8px 0' }} />

            <Form.Item
              label={t('reports.pdfOrientation') || 'A4 方向'}
              name="pdfOrientation"
              tooltip={t('reports.pdfOrientationTooltip') || 'PDF 導出時的紙張方向'}
            >
              <Radio.Group>
                <Radio value="landscape">
                  {t('reports.landscape') || '橫向'} (297mm × 210mm)
                </Radio>
                <Radio value="portrait">
                  {t('reports.portrait') || '縱向'} (210mm × 297mm)
                </Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label={t('reports.imageQuality') || '圖片質量'}
              name="imageQuality"
              tooltip={t('reports.imageQualityTooltip') || 'PDF 導出時圖片的壓縮質量（0.1-1.0），值越大質量越高但文件也越大'}
            >
              <InputNumber
                min={0.1}
                max={1.0}
                step={0.1}
                style={{ width: '100%' }}
                placeholder="0.9"
                precision={1}
              />
            </Form.Item>
          </div>
        </Space>
      </Form>
    </Modal>
  );
};

export default GallerySettings;
